import { useEffect, useMemo, useState } from "react";
import { useProfile } from "@/hooks/useProfile";
import {
  type UnitSystem,
  getStoredUnits,
  hasStoredUnits,
  resolveUnitSystem,
  UNITS_CHANGED_EVENT,
  formatDistanceWithUnit,
  formatPaceDisplay,
  paceUnitLabel,
  formatWeightWithUnit,
  weightUnitLabel,
  formatMetersLabel,
  kmToDisplayDistance,
} from "@/lib/units";

/** Live unit preference from profile + device storage (optimistic on toggle). */
export function useUnits(): UnitSystem {
  const { profile } = useProfile();
  const [optimistic, setOptimistic] = useState<UnitSystem | null>(null);

  // Seed device storage from profile when nothing is stored yet
  useEffect(() => {
    const profileUnits = profile?.units;
    if (
      (profileUnits === "imperial" || profileUnits === "metric") &&
      !hasStoredUnits()
    ) {
      try {
        localStorage.setItem("straiv_units", profileUnits);
      } catch {}
    }
  }, [profile?.units]);

  // After a toggle, keep showing the new units until profile.units catches up
  useEffect(() => {
    const onChange = (e: Event) => {
      const d = (e as CustomEvent<UnitSystem>).detail;
      if (d === "imperial" || d === "metric") setOptimistic(d);
    };
    window.addEventListener(UNITS_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(UNITS_CHANGED_EVENT, onChange);
  }, []);

  useEffect(() => {
    if (optimistic && profile?.units === optimistic) {
      setOptimistic(null);
    }
  }, [profile?.units, optimistic]);

  // When profile changes from server (e.g. other device) and we're not mid-toggle, sync storage
  useEffect(() => {
    const profileUnits = profile?.units;
    if (
      !optimistic &&
      (profileUnits === "imperial" || profileUnits === "metric") &&
      getStoredUnits() !== profileUnits
    ) {
      try {
        localStorage.setItem("straiv_units", profileUnits);
      } catch {}
    }
  }, [profile?.units, optimistic]);

  return useMemo(
    () => optimistic ?? resolveUnitSystem(profile?.units ?? getStoredUnits()),
    [optimistic, profile?.units],
  );
}

export function useUnitFormatters() {
  const units = useUnits();
  return useMemo(
    () => ({
      units,
      formatKm: (km: number, digits?: number) => formatDistanceWithUnit(km, units, { digits }),
      formatMeters: (m: number) => formatMetersLabel(m, units),
      formatPace: (secPerKm: number) => formatPaceDisplay(secPerKm, units),
      paceSuffix: paceUnitLabel(units),
      formatWeight: (kg: number) => formatWeightWithUnit(kg, units),
      weightLabel: weightUnitLabel(units),
      toDisplayDistance: (km: number) => kmToDisplayDistance(km, units),
    }),
    [units],
  );
}
